import {
  Breadcrumbs,
  CenteredContainer,
  Column,
  Container,
  ElasticContentContainer,
  EmptyScreen,
  Row,
  ScrollableWrapper
} from '@cdab/scania/qpr/components'
import { getClient } from '@cdab/scania/qpr/contexts/backend-provider'
import { useTitle } from '@cdab/scania/qpr/contexts/title'
import {
  useCreateUser,
  useCssVariableBreakpoint,
  useValidatePassword
} from '@cdab/scania/qpr/hooks'
import type { Language, Market, User } from '@cdab/scania/qpr/schema'
import {
  AuditReminderIntervals,
  EditRights,
  Roles
} from '@cdab/scania/qpr/schema'
import { useCallback, useEffect, useState } from 'react'
import {
  isRouteErrorResponse,
  json,
  useLoaderData,
  useNavigate,
  useRouteError
} from 'react-router'

import {
  ValidateEmail,
  capitalizeFirstLetter,
  checkStringLength,
  enumToTranslationText
} from '@cdab/utils'
import type { TdsDropdownCustomEvent } from '@scania/tegel'
import {
  TdsCheckbox,
  TdsDivider,
  TdsDropdown,
  TdsDropdownOption,
  TdsIcon,
  TdsMessage,
  TdsTextField,
  TdsToast,
  TdsToggle
} from '@scania/tegel-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import {
  ButtonMessageWrapper,
  CheckboxWrapper,
  StyledButton,
  StyledColumn,
  StyledFirstButton,
  StyledRow,
  StyledSectionTitle,
  Title,
  ToggleWrapper
} from './users.styles'

interface UserCreateData
  extends Pick<
    User,
    | 'firstName'
    | 'lastName'
    | 'userName'
    | 'email'
    | 'cellPhone'
    | 'country'
    | 'languageId'
    | 'editRights'
    | 'alertOnCertify'
    | 'auditReminderInterval'
    | 'defaultMarketId'
    | 'truck'
    | 'bus'
    | 'engine'
    | 'active'
    | 'viewId'
  > {
  roleId: number
  markets: number[]
  password: string
}

export type NewAdminUsersLoaderData = {
  languages: Language[]
  allMarkets: Market[]
}

export async function loader(): Promise<NewAdminUsersLoaderData> {
  try {
    const client = getClient()
    const userInfo = await client.GetuserInfo()
    if (!userInfo) {
      throw json({
        status: 401
      })
    }

    const [currentUser, languages, allMarkets] = await Promise.all([
      client.UserService.GetUser(userInfo.userId),
      client.UserService.GetLanguagesForUser(),
      client.MarketsService.GetAllMarkets()
    ])

    if (currentUser.role < Roles.FactoryEngineAdmin) {
      throw json({
        status: 403
      })
    }

    return {
      languages,
      allMarkets
    }
  } catch (error) {
    if (error instanceof Response) {
      throw new Response(null, {
        status: error.status
      })
    } else {
      throw new Response(null, {
        status: 404
      })
    }
  }
}

export function Page() {
  const navigate = useNavigate()
  const { t } = useTranslation(['user', 'common'])
  const { updateTitles } = useTitle()
  const isLg = useCssVariableBreakpoint('--tds-grid-lg')
  const { languages, allMarkets } = useLoaderData() as NewAdminUsersLoaderData

  const [data, setData] = useState<UserCreateData>({
    userName: '',
    firstName: '',
    lastName: '',
    password: '',
    cellPhone: '',
    country: '',
    defaultMarketId: 0,
    email: '',
    active: true,
    alertOnCertify: false,
    auditReminderInterval: 6,
    viewId: 6,
    languageId: 6,
    truck: false,
    bus: false,
    engine: false,
    roleId: Roles.Auditor,
    editRights: EditRights.ReadOnly,
    markets: [1456] //testmarket
  })

  const [submitState, handleClickCreateUser, setSubmitState] = useCreateUser({
    ...data,
    editRightId: data.editRights
  })

  const [statePassword, handleDoValidatePassword] = useValidatePassword()

  const [stateUserName, setStateUserName] = useState<boolean>(true)
  const [stateFirstName, setStateFirstName] = useState<boolean>(true)
  const [stateLastName, setStateLastName] = useState<boolean>(true)
  const [stateEmail, setStateEmail] = useState<boolean>(true)
  const [stateCellPhone, setStateCellPhone] = useState<boolean>(true)
  const [stateCountry, setStateCountry] = useState<boolean>(true)
  const [stateMarkets, setStateMarkets] = useState<boolean>(true)

  const checkAllData = useCallback(() => {
    return (
      stateUserName &&
      stateFirstName &&
      stateLastName &&
      statePassword.status === 'success' &&
      stateEmail &&
      stateCountry &&
      stateCellPhone &&
      stateMarkets
    )
  }, [
    stateUserName,
    stateFirstName,
    stateLastName,
    statePassword.status,
    stateEmail,
    stateCountry,
    stateCellPhone,
    stateMarkets
  ])

  const validateAllData = useCallback(() => {
    setStateFirstName(checkStringLength(data.firstName, 2))
    setStateLastName(checkStringLength(data.lastName, 2))
    handleDoValidatePassword(data.password)
    setStateEmail(ValidateEmail(data.email))
    setStateCellPhone(checkStringLength(data.cellPhone, 5, true))
    setStateCountry(checkStringLength(data.country, 4, true))
    setStateMarkets(data.markets.length > 0)

    return checkAllData()
  }, [
    checkAllData,
    data.cellPhone,
    data.country,
    data.email,
    data.firstName,
    data.lastName,
    data.markets.length,
    data.password,
    handleDoValidatePassword
  ])

  const onChangeMarkets = useCallback(
    (
      e: TdsDropdownCustomEvent<{
        name: string
        value: string | null
      }>
    ) => {
      const value = e.detail.value
      if (value === null) {
        setData({
          ...data,
          markets: []
        })
        setStateMarkets(false)
        return
      }

      const selectedMarketIds = value
        .split(',')
        .map(v => v.trim())
        .filter(v => v !== '')
        .map(v => parseInt(v, 10))
        .filter(v => !Number.isNaN(v))

      setData({
        ...data,
        markets: selectedMarketIds
      })
      setStateMarkets(selectedMarketIds.length > 0)
    },
    [data]
  )

  useEffect(() => {
    updateTitles({
      contentHeader: {
        subtitle: t('users', { ns: 'common' }),
        title: t('administration', { ns: 'common' })
      },
      mobile: {
        title: `${t('users', { ns: 'common' })}`,
        description: null
      }
    })
  }, [t])

  useEffect(() => {
    if (submitState.status === 'success') {
      toast(
        <TdsToast
          variant='success'
          header={t('user-created', { ns: 'common' })}
          subheader={submitState.message}
        />
      )
      setSubmitState({ status: 'none', message: null })
      navigate('/admin/users/all')
    }
  }, [navigate, submitState.status, submitState.message, setSubmitState, t])

  return (
    <ElasticContentContainer overflowHidden={true}>
      <ScrollableWrapper>
        <Container
          fullHeight
          fluid='normal'
          paddingOnColumns={isLg}
          paddingOnContainer={isLg}
        >
          <Row>
            <Column width={12} className='tds-u-pt2'>
              <Breadcrumbs
                links={[
                  {
                    text: t('users', { ns: 'common' }),
                    to: `/admin/users/all`
                  },
                  { text: t('new', { ns: 'common' }), to: '' }
                ]}
              />
            </Column>
          </Row>
          <Row>
            <Column width={12} lg={8} offset={{ lg: 2 }}>
              <StyledRow>
                <Column width={12} lg={6} padding={isLg}>
                  <Title>{t('profile')}</Title>
                </Column>
              </StyledRow>
              <StyledRow>
                <StyledColumn width={12} lg={6} padding={isLg}>
                  <TdsTextField
                    modeVariant='secondary'
                    label={t('first-name')}
                    labelPosition='outside'
                    maxLength={50}
                    name='firstName'
                    placeholder={t('first-name')}
                    value={data.firstName}
                    disabled={false}
                    onTdsInput={e => {
                      setData({ ...data, firstName: e.target.value })
                    }}
                    onTdsBlur={e =>
                      setStateFirstName(checkStringLength(e.target.value, 2))
                    }
                    state={!stateFirstName ? 'error' : undefined}
                    helper={
                      !stateFirstName
                        ? t('message.validation-error', { chars: '2' })
                        : undefined
                    }
                  />
                </StyledColumn>
                <StyledColumn width={12} lg={6} padding={isLg}>
                  <TdsTextField
                    modeVariant='secondary'
                    label={t('last-name')}
                    labelPosition='outside'
                    maxLength={50}
                    name='lastName'
                    placeholder={t('last-name')}
                    value={data.lastName}
                    disabled={false}
                    onTdsInput={e => {
                      setData({ ...data, lastName: e.target.value })
                    }}
                    onTdsBlur={e =>
                      setStateLastName(checkStringLength(e.target.value, 2))
                    }
                    state={!stateLastName ? 'error' : undefined}
                    helper={
                      !stateLastName
                        ? t('message.validation-error', { chars: '2' })
                        : undefined
                    }
                  />
                </StyledColumn>
              </StyledRow>
              <StyledRow>
                <StyledColumn width={12} lg={6} padding={isLg}>
                  <TdsTextField
                    modeVariant='secondary'
                    label={t('user-name')}
                    labelPosition='outside'
                    maxLength={50}
                    name='userName'
                    placeholder={t('user-name')}
                    value={data.userName}
                    disabled={false}
                    onTdsInput={e => {
                      setData({
                        ...data,
                        userName: e.target.value
                      })
                    }}
                    onTdsBlur={e =>
                      setStateUserName(checkStringLength(e.target.value, 3))
                    }
                    state={!stateUserName ? 'error' : undefined}
                    helper={
                      !stateUserName
                        ? t('message.validation-error', { chars: '3' })
                        : undefined
                    }
                  />
                </StyledColumn>
                <StyledColumn width={12} lg={6} padding={isLg}>
                  <TdsTextField
                    modeVariant='secondary'
                    label={t('email')}
                    labelPosition='outside'
                    maxLength={50}
                    name='email'
                    placeholder={t('email')}
                    value={data.email}
                    disabled={false}
                    onTdsInput={e => {
                      setData({ ...data, email: e.target.value })
                    }}
                    onTdsBlur={e =>
                      setStateEmail(ValidateEmail(e.target.value))
                    }
                    state={!stateEmail ? 'error' : undefined}
                    helper={
                      !stateEmail
                        ? t('message.email-validation-error')
                        : undefined
                    }
                  />
                </StyledColumn>
              </StyledRow>
              <StyledRow>
                <StyledColumn width={12} lg={6} padding={isLg}>
                  <TdsTextField
                    modeVariant='secondary'
                    label={t('password')}
                    labelPosition='outside'
                    type='password'
                    maxLength={64}
                    name='password'
                    placeholder={t('password')}
                    value={data.password}
                    disabled={false}
                    onTdsInput={e => {
                      setData({ ...data, password: e.target.value })
                    }}
                    onTdsBlur={e => handleDoValidatePassword(e.target.value)}
                    state={
                      statePassword.status === 'error' ? 'error' : undefined
                    }
                    helper={statePassword.message || undefined}
                  />
                </StyledColumn>
              </StyledRow>
              <StyledRow>
                <StyledColumn width={12} lg={6} padding={isLg}>
                  <TdsTextField
                    modeVariant='secondary'
                    label={t('mobile-phone')}
                    labelPosition='outside'
                    maxLength={20}
                    name='cellPhone'
                    placeholder={t('mobile-phone')}
                    value={data.cellPhone ?? ''}
                    disabled={false}
                    onTdsInput={e => {
                      setData({ ...data, cellPhone: e.target.value })
                    }}
                    onTdsBlur={e =>
                      setStateCellPhone(
                        checkStringLength(e.target.value, 5, true)
                      )
                    }
                    state={!stateCellPhone ? 'error' : undefined}
                    helper={
                      !stateCellPhone
                        ? t('message.validation-error', { chars: '5' })
                        : undefined
                    }
                  />
                </StyledColumn>
                <StyledColumn width={12} lg={6} padding={isLg}>
                  <TdsTextField
                    modeVariant='secondary'
                    label={t('country')}
                    labelPosition='outside'
                    maxLength={40}
                    name='country'
                    placeholder={t('country')}
                    value={data.country ?? ''}
                    disabled={false}
                    onTdsInput={e =>
                      setData({ ...data, country: e.target.value })
                    }
                    onTdsBlur={e =>
                      setStateCountry(
                        checkStringLength(e.target.value, 4, true)
                      )
                    }
                    state={!stateCountry ? 'error' : undefined}
                    helper={
                      !stateCountry
                        ? t('message.validation-error', { chars: '4' })
                        : undefined
                    }
                  />
                </StyledColumn>
              </StyledRow>
              <TdsDivider variant='discrete' />
              <StyledRow>
                <Column width={12} lg={6} padding={isLg}>
                  <Title>{t('preferences')}</Title>
                </Column>
              </StyledRow>
              <StyledRow>
                <StyledColumn width={12} lg={6} padding={isLg}>
                  <TdsDropdown
                    modeVariant='secondary'
                    id='editRightsId'
                    label={t('edit-rights')}
                    size='lg'
                    labelPosition='outside'
                    placeholder={t('edit-rights')}
                    defaultValue={data.editRights.toString()}
                    openDirection={isLg ? 'down' : 'auto'}
                    onTdsChange={e => {
                      const editRights = parseInt(e.detail.value ?? '')
                      if (Number.isNaN(editRights)) return

                      setData({
                        ...data,
                        editRights: editRights
                      })
                    }}
                  >
                    {(
                      Object.keys(EditRights) as Array<keyof typeof EditRights>
                    ).map((key, index) => {
                      if (parseInt(key) >= 0) {
                        return (
                          <TdsDropdownOption
                            key={index}
                            value={index.toString()}
                          >
                            {t(
                              enumToTranslationText(EditRights[key].toString())
                            )}
                          </TdsDropdownOption>
                        )
                      }

                      return null
                    })}
                  </TdsDropdown>
                </StyledColumn>
                <StyledColumn width={12} lg={6} padding={isLg}>
                  <TdsDropdown
                    modeVariant='secondary'
                    id='roleId'
                    label={t('role')}
                    size='lg'
                    labelPosition='outside'
                    placeholder={t('role')}
                    defaultValue={data.roleId.toString()}
                    openDirection={isLg ? 'down' : 'auto'}
                    onTdsChange={e => {
                      const roleId = parseInt(e.detail.value ?? '')
                      if (Number.isNaN(roleId)) return

                      setData({
                        ...data,
                        roleId: roleId
                      })
                    }}
                  >
                    {(Object.keys(Roles) as Array<keyof typeof Roles>).map(
                      (key, index) => {
                        if (parseInt(key)) {
                          return (
                            <TdsDropdownOption
                              key={index}
                              value={key.toString()}
                            >
                              {t(enumToTranslationText(Roles[key].toString()))}
                            </TdsDropdownOption>
                          )
                        }
                        return null
                      }
                    )}
                  </TdsDropdown>
                </StyledColumn>
              </StyledRow>
              <StyledRow>
                <StyledColumn width={12} lg={6} padding={isLg}>
                  <TdsDropdown
                    modeVariant='secondary'
                    id='market'
                    label={capitalizeFirstLetter(t('market', { ns: 'common' }))}
                    size='lg'
                    labelPosition='outside'
                    placeholder={capitalizeFirstLetter(
                      t('market', { ns: 'common' })
                    )}
                    defaultValue={data.markets.join(',')}
                    multiselect
                    onTdsChange={onChangeMarkets}
                    error={!stateMarkets}
                    openDirection={isLg ? 'down' : 'auto'}
                    helper={
                      !stateMarkets
                        ? t('message.validation-required')
                        : undefined
                    }
                  >
                    {allMarkets
                      .sort((a: Market, b: Market) =>
                        a.marketName.localeCompare(b.marketName)
                      )
                      .map(({ id, marketName }) => {
                        return (
                          <TdsDropdownOption key={id} value={id.toString()}>
                            {marketName}
                          </TdsDropdownOption>
                        )
                      })}
                  </TdsDropdown>
                  <ToggleWrapper>
                    <TdsToggle
                      checked={data.active ?? false}
                      id='activeToggle'
                      onChange={() =>
                        setData({
                          ...data,
                          active: !data.active
                        })
                      }
                    >
                      <div slot='label'>{t('active')}</div>
                    </TdsToggle>
                  </ToggleWrapper>
                </StyledColumn>
                <StyledColumn width={12} lg={6} padding={isLg}>
                  <StyledSectionTitle>{t('products')}</StyledSectionTitle>
                  <CheckboxWrapper>
                    <TdsCheckbox
                      id='busCheckbox'
                      onTdsChange={() =>
                        setData({
                          ...data,
                          bus: !data.bus
                        })
                      }
                      checked={data.bus === true}
                    >
                      <div slot='label'>{t('bus')}</div>
                    </TdsCheckbox>
                  </CheckboxWrapper>
                  <CheckboxWrapper>
                    <TdsCheckbox
                      id='engineCheckbox'
                      onTdsChange={() =>
                        setData({
                          ...data,
                          engine: !data.engine
                        })
                      }
                      checked={data.engine === true}
                    >
                      <div slot='label'>{t('engine')}</div>
                    </TdsCheckbox>
                  </CheckboxWrapper>
                  <CheckboxWrapper>
                    <TdsCheckbox
                      id='truckCheckbox'
                      onTdsChange={() =>
                        setData({
                          ...data,
                          truck: !data.truck
                        })
                      }
                      checked={data.truck === true}
                    >
                      <div slot='label'>{t('truck')}</div>
                    </TdsCheckbox>
                  </CheckboxWrapper>
                </StyledColumn>
              </StyledRow>
              <TdsDivider variant='discrete' />
              <StyledRow>
                <Column width={12} lg={6} padding={isLg}>
                  <Title>{t('settings')}</Title>
                </Column>
              </StyledRow>
              <StyledRow>
                <StyledColumn width={12} lg={6} padding={isLg}>
                  <TdsDropdown
                    modeVariant='secondary'
                    id='languageId'
                    label={t('language')}
                    size='lg'
                    labelPosition='outside'
                    placeholder={t('language')}
                    defaultValue={data.languageId.toString()}
                    openDirection={isLg ? 'down' : 'auto'}
                    onTdsChange={e => {
                      const languageId = parseInt(e.detail.value ?? '')
                      if (Number.isNaN(languageId)) return

                      setData({
                        ...data,
                        languageId: languageId
                      })
                    }}
                  >
                    {languages
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map(language => (
                        <TdsDropdownOption
                          key={language.id}
                          value={language.id.toString()}
                        >
                          {language.name.trim()}
                        </TdsDropdownOption>
                      ))}
                  </TdsDropdown>
                </StyledColumn>
                <StyledColumn width={12} lg={6} padding={isLg}>
                  <TdsDropdown
                    modeVariant='secondary'
                    id='auditReminderInterval'
                    label={t('audit-reminder-interval')}
                    size='lg'
                    labelPosition='outside'
                    placeholder={t('audit-reminder-interval')}
                    defaultValue={
                      data.auditReminderInterval
                        ? data.auditReminderInterval.toString()
                        : 'null'
                    }
                    openDirection={isLg ? 'down' : 'auto'}
                    onTdsChange={e => {
                      const auditReminderInterval = parseInt(
                        e.detail.value ?? ''
                      )
                      if (Number.isNaN(auditReminderInterval)) return

                      setData({
                        ...data,
                        auditReminderInterval: auditReminderInterval
                      })
                    }}
                  >
                    {AuditReminderIntervals.map(interval => (
                      <TdsDropdownOption key={interval.id} value={interval.id}>
                        {interval.name}
                      </TdsDropdownOption>
                    ))}
                  </TdsDropdown>
                </StyledColumn>
              </StyledRow>
              <StyledRow>
                <StyledColumn width={12} lg={6} padding={isLg}>
                  <TdsDropdown
                    modeVariant='secondary'
                    id='defaultMarketId'
                    label={t('main-market')}
                    size='lg'
                    labelPosition='outside'
                    placeholder={t('main-market')}
                    defaultValue={data.defaultMarketId.toString()}
                    openDirection={isLg ? 'down' : 'auto'}
                    onTdsChange={e => {
                      const defaultMarketId = parseInt(e.detail.value ?? '')
                      if (Number.isNaN(defaultMarketId)) return

                      setData({
                        ...data,
                        defaultMarketId: defaultMarketId
                      })
                    }}
                  >
                    {allMarkets
                      .filter(m => data.markets.includes(m.id))
                      .map(market => (
                        <TdsDropdownOption
                          key={market.id}
                          value={market.id.toString()}
                        >
                          {market.marketName}
                        </TdsDropdownOption>
                      ))}
                  </TdsDropdown>
                </StyledColumn>
                <StyledColumn width={12} lg={6} padding={isLg}>
                  <ToggleWrapper>
                    <TdsToggle
                      checked={data.alertOnCertify ?? false}
                      id='alertOnCertifyToggle'
                      onChange={() =>
                        setData({
                          ...data,
                          alertOnCertify: !data.alertOnCertify
                        })
                      }
                    >
                      <div slot='label'>{t('alert-on-certify')}</div>
                    </TdsToggle>
                  </ToggleWrapper>
                </StyledColumn>
              </StyledRow>
              <StyledRow>
                <Column width={12} lg={12} padding={isLg}>
                  {submitState.status === 'error' && (
                    <TdsMessage
                      modeVariant='secondary'
                      header={submitState.message}
                      variant={submitState.status}
                    />
                  )}
                </Column>
              </StyledRow>
              <StyledRow>
                <Column width={12} lg={12} padding={isLg}>
                  <ButtonMessageWrapper>
                    <StyledFirstButton
                      text={t('cancel', {
                        ns: 'common'
                      })}
                      variant='secondary'
                      onClick={() => navigate('/admin/users/all')}
                    />
                    <StyledButton
                      disabled={!checkAllData()}
                      text={capitalizeFirstLetter(t('save', { ns: 'common' }))}
                      onClick={() => {
                        validateAllData() && handleClickCreateUser()
                      }}
                    />
                  </ButtonMessageWrapper>
                </Column>
              </StyledRow>
            </Column>
          </Row>
        </Container>
      </ScrollableWrapper>
    </ElasticContentContainer>
  )
}

export function ErrorBoundary() {
  const { t } = useTranslation('errors')
  const isLg = useCssVariableBreakpoint('--tds-grid-lg')
  const error = useRouteError()

  if (isRouteErrorResponse(error)) {
    let message = t('talk-to-admin')

    if (error.status === 403) {
      message = t('access-denied')
    }

    return (
      <CenteredContainer
        fluid='normal'
        paddingOnColumns={!isLg}
        paddingOnContainer={!isLg}
      >
        <Row>
          <Column fullHeight width={12} padding={false}>
            <EmptyScreen
              title={t('could-not-load-page')}
              description={message}
              icon={<TdsIcon name='warning' size='lg' />}
            />
          </Column>
        </Row>
      </CenteredContainer>
    )
  }

  // rethrow to let the parent error boundary handle it
  // when it's not a special case for this route
  throw error
}
